import { FC, useContext, useEffect, useState } from 'react';
import { isRight } from 'fp-ts/lib/Either';
import di from '../../../../di/DependencyInjection';
import BucketEntity from '../../../../domain/entities/BucketEntity';
import UserEntity from '../../../../domain/entities/UserEntity';
import GetAllBucketsUseCase, { GetAllBucketsUseCaseName } from '../../../../domain/use_cases/bucket/GetAllBucketsUseCase';
import GetUsersInBucketUseCase, { GetUsersInBucketUseCaseName } from '../../../../domain/use_cases/bucket/GetUsersInBucketUseCase';
import BucketTreeNode from './BucketTreeNode';
import BucketUserPanel from './BucketUserPanel';
import LoadingComponent from '../../../components/LoadingComponent/LoadingComponent';
import ButtonComponent from '../../../components/button/ButtonComponent';
import { ButtonType } from '../../../components/button/ButtonComponentProps';
import { MdRefresh } from 'react-icons/md';
import UserContext from '../../../../domain/providers/user/UserContext';
import UserContextType from '../../../../domain/providers/user/UserContextType';
import './BucketTreePage.scss';

const buildTree = (flatBuckets: BucketEntity[]): BucketEntity[] => {
    const map = new Map<number, BucketEntity>();
    const roots: BucketEntity[] = [];

    flatBuckets.forEach(b => {
        map.set(b.bucket_id, { ...b, children: [] });
    });

    flatBuckets.forEach(b => {
        const node = map.get(b.bucket_id)!;
        if (b.parent_bucket === null) {
            roots.push(node);
        } else {
            const parent = map.get(b.parent_bucket);
            if (parent) {
                parent.children!.push(node);
            } else {
                if (roots.length > 0) {
                    roots[0].children!.push(node);
                } else {
                    roots.push(node);
                }
            }
        }
    });

    return roots;
};

const getBreadcrumb = (bucketId: number, flatBuckets: BucketEntity[]): string[] => {
    const path: string[] = [];
    const bucketMap = new Map(flatBuckets.map(b => [b.bucket_id, b]));
    let current = bucketMap.get(bucketId);
    while (current) {
        path.unshift(current.name);
        current = current.parent_bucket !== null
            ? bucketMap.get(current.parent_bucket)
            : undefined;
    }
    return path;
};

const countTotalBuckets = (tree: BucketEntity[]): number => {
    return tree.reduce((sum, b) => sum + 1 + (b.children ? countTotalBuckets(b.children) : 0), 0);
};

const BucketTreePage: FC<{}> = () => {
    const [tree, setTree] = useState<BucketEntity[]>([]);
    const [flatBuckets, setFlatBuckets] = useState<BucketEntity[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [selectedBucket, setSelectedBucket] = useState<BucketEntity | null>(null);
    const [users, setUsers] = useState<UserEntity[]>([]);
    const [usersLoading, setUsersLoading] = useState<boolean>(false);
    const [searchTerm, setSearchTerm] = useState<string>('');
    const [breadcrumb, setBreadcrumb] = useState<string[]>([]);
    const { user } = useContext(UserContext) as UserContextType;

    const currentUserEmail = user?.email ?? '';

    let _searchTimer: any;

    const _loadBuckets = async () => {
        setLoading(true);
        const response = await di.get<GetAllBucketsUseCase>(GetAllBucketsUseCaseName).call();
        if (isRight(response)) {
            // API now returns user_count per bucket directly
            setFlatBuckets(response.right);
            setTree(buildTree(response.right));
        }
        setLoading(false);
    };

    const _handleSelectBucket = async (bucket: BucketEntity) => {
        setSelectedBucket(bucket);
        setBreadcrumb(getBreadcrumb(bucket.bucket_id, flatBuckets));
        setUsersLoading(true);
        const response = await di.get<GetUsersInBucketUseCase>(GetUsersInBucketUseCaseName).call(bucket.bucket_id, 1, 200);
        if (isRight(response)) {
            setUsers(response.right);
        } else {
            setUsers([]);
        }
        setUsersLoading(false);
    };

    const _handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        clearTimeout(_searchTimer);
        _searchTimer = setTimeout(() => {
            setSearchTerm(value.toLowerCase());
        }, 500);
    };

    const _handleRefresh = () => {
        setSelectedBucket(null);
        setUsers([]);
        setBreadcrumb([]);
        _loadBuckets();
    };

    const _handleUserMoved = () => {
        if (selectedBucket) {
            _handleSelectBucket(selectedBucket);
        }
        _loadBuckets();
    };

    useEffect(() => {
        _loadBuckets();
    }, []);

    if (loading) return <div className="container"><LoadingComponent /></div>;

    const totalBuckets = countTotalBuckets(tree);
    const selectedUserCount = users.length;

    return (
        <div className="container bucket_tree_page">
            <div className="bucket_tree_header">
                <div className="bucket_tree_header_left">
                    <h5 className="bucket_tree_title">Bucket Tree</h5>
                    <div className="bucket_tree_summary">
                        <span className="summary_item">{totalBuckets} buckets</span>
                        {selectedBucket && (
                            <>
                                <span className="summary_divider">·</span>
                                <span className="summary_item">{selectedUserCount} users in {selectedBucket.name}</span>
                            </>
                        )}
                    </div>
                </div>
                <div className="bucket_tree_actions">
                    <input
                        type="text"
                        className="form-control bucket_tree_search"
                        placeholder="Search buckets..."
                        onChange={_handleSearch}
                    />
                    <ButtonComponent
                        text="Refresh"
                        type={ButtonType.MAIN}
                        onClick={_handleRefresh}
                        icon={<MdRefresh />}
                    />
                </div>
            </div>

            <div className="row bucket_tree_content">
                <div className="col-12 col-md-5 bucket_tree_panel">
                    <div className="bucket_tree_scroll">
                        {tree.map(bucket => (
                            <BucketTreeNode
                                key={bucket.bucket_id}
                                bucket={bucket}
                                depth={0}
                                selectedBucketId={selectedBucket?.bucket_id ?? null}
                                onSelect={_handleSelectBucket}
                                searchTerm={searchTerm}
                            />
                        ))}
                        {tree.length === 0 && (
                            <div className="text-muted p-3">No buckets found</div>
                        )}
                    </div>
                </div>

                <div className="col-12 col-md-7">
                    <BucketUserPanel
                        selectedBucket={selectedBucket}
                        users={users}
                        loading={usersLoading}
                        allBuckets={flatBuckets}
                        onUserMoved={_handleUserMoved}
                        breadcrumb={breadcrumb}
                        currentUserEmail={currentUserEmail}
                    />
                </div>
            </div>
        </div>
    );
};

export default BucketTreePage;
